"use client";
import { IAccountAssets, IBlocks, IUserData } from "@/types";
import React, { useState } from "react";
import CardSetting from "@/components/admin/settings/CardSetting";
import { Button } from "@/components/ui/button";
import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { cn, replaceProtocolWithHTTPS, startsWithHTTPS } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
interface Props {
    account: IUserData;
    block: IBlocks;
    blockUpdate: React.Dispatch<React.SetStateAction<IBlocks>>;
    update: (value: IBlocks) => void;
    assets: IAccountAssets[];
    impersonate?: any;
}

export default function FourthwallOptions({
    account,
    block,
    blockUpdate,
    update,
    assets,
    impersonate,
}: Props) {
    const [items, setItems] = React.useState<any>({
        uniqueItems: [],
    });
    const [url, setUrl] = React.useState(block.url);
    const [oldBlock, setOldBlock] = React.useState(block);
    const [selectedItems, setSelectedItems] = useState<any[]>(
        block.options?.items || []
    );
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_API_SERVER}/fourthwall/${block.url.replaceAll("https://", "")}/unique-items`,
                    { cache: "no-store" }
                );
                const data = await response.json();
                if (!data.error) {
                    setItems(data);
                } else {
                    setItems({
                        uniqueItems: [],
                    });
                }
            } catch (error) {
                console.error(error);
                setItems({
                    uniqueItems: [],
                });
            }
        };
        fetchData();
    }, [block.url, account]);
    // Move checked items only
    const moveItem = (index: number, direction: "up" | "down") => {
        const newItems = [...selectedItems];

        // Swap items in the array
        if (direction === "up" && index > 0) {
            [newItems[index], newItems[index - 1]] = [
                newItems[index - 1],
                newItems[index],
            ];
        } else if (direction === "down" && index < newItems.length - 1) {
            [newItems[index], newItems[index + 1]] = [
                newItems[index + 1],
                newItems[index],
            ];
        }

        // Update the 'sort' property based on the new order
        const updatedItems = newItems.map((item, i) => ({
            ...item,
            sort: i, // Update the sort based on the index
        }));

        setSelectedItems(updatedItems); // Update the state
        update({
            ...block,
            options: {
                ...block.options,
                items: updatedItems, // Update items with new order
            },
        });
    };

    React.useEffect(() => {
        if (block.options?.items) {
            setSelectedItems(block.options?.items);
        }
    }, [block.options?.items]);

    const addItem = (item: any) => {
        const newItems = [
            ...selectedItems,
            { sort: selectedItems.length, id: item.id },
        ];
        setSelectedItems(newItems);
        update({
            ...block,
            options: {
                ...block.options,
                items: newItems,
            },
        });
    };

    const removeItem = (id: string) => {
        const newItems = selectedItems.filter((item) => item.id !== id);
        setSelectedItems(newItems);
        update({
            ...block,
            options: {
                ...block.options,
                items: newItems,
            },
        });
    };

    return (
        <CardSetting noBorder>
            <div className="border-3 flex w-full flex-col gap-3 border-white">
                <Input
                    label="Store URL"
                    variant="card"
                    className="w-full"
                    placeholder="https://merch.doras.to"
                    required
                    value={url}
                    onChange={(e) => {
                        setUrl(e.target.value);
                        blockUpdate({
                            ...block,
                            url: e.target.value,
                        });
                    }}
                    disabled={selectedItems.length > 0}
                    onBlur={(e) => {
                        if (!startsWithHTTPS(e.target.value)) {
                            e.target.value = replaceProtocolWithHTTPS(
                                e.target.value
                            );
                            setUrl(e.target.value);
                            blockUpdate({
                                ...block,
                                url: e.target.value,
                            });
                        }
                        if (oldBlock.url !== e.target.value) {
                            update({
                                ...block,
                                url: e.target.value,
                            });
                            setOldBlock({
                                ...oldBlock,
                                url: e.target.value,
                            });
                        }
                    }}
                />
                <div className="relative flex w-full flex-col gap-4">
                    <Separator />
                    <div className="flex items-center justify-between">
                        <Label variant={"toggle"}>Products</Label>
                        <Label variant={"subtoggle"}>
                            {selectedItems.length}/10
                        </Label>
                    </div>
                    <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                        <Label
                            variant={"toggle"}
                            className="mx-auto pb-4 text-center"
                        >
                            Visible
                        </Label>

                        {selectedItems
                            .sort((a, b) => a.sort - b.sort) // Sort selected items by 'sort' property
                            .map((item: any, index) => {
                                const itemData = items?.uniqueItems?.find(
                                    (i: any) => i.id === item.id
                                );
                                return (
                                    <div
                                        key={item.id}
                                        className="group/parent relative flex items-center gap-2"
                                    >
                                        <div className="flex flex-col gap-0.5">
                                            <Button
                                                variant="ghost"
                                                className="aspect-square h-6 w-6 p-1"
                                                onClick={() =>
                                                    moveItem(index, "up")
                                                }
                                                disabled={index === 0}
                                            >
                                                <IconArrowUp size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                className="aspect-square h-6 w-6 p-1"
                                                onClick={() =>
                                                    moveItem(index, "down")
                                                }
                                                disabled={
                                                    index ===
                                                    selectedItems.length - 1
                                                }
                                            >
                                                <IconArrowDown size={16} />
                                            </Button>
                                        </div>
                                        <label className="flex w-full select-none items-center gap-3 p-2 hover:bg-accent">
                                            <img
                                                src={itemData?.image}
                                                alt={itemData?.title}
                                                className="h-12 w-12 rounded-md object-cover"
                                            />
                                            <div className="flex flex-col">
                                                <span className="font-medium">
                                                    {itemData?.title}
                                                </span>
                                            </div>
                                            <Checkbox
                                                className="ml-auto"
                                                checked={true}
                                                onCheckedChange={() =>
                                                    removeItem(item.id)
                                                }
                                            />
                                        </label>
                                    </div>
                                );
                            })}
                        <div className="flex flex-col gap-2 py-4">
                            <Label variant={"toggle"} className="">
                                Add products
                            </Label>
                            {selectedItems.length === 10 && (
                                <Label variant={"subtoggle"} className="">
                                    You cannot display any more products as
                                    you've already selected 10. Please remove
                                    some products to add more.
                                </Label>
                            )}
                        </div>
                        {items?.uniqueItems
                            .filter(
                                (item: any) =>
                                    !selectedItems.some(
                                        (selected) => selected.id === item.id
                                    )
                            )
                            .map((item: any) => {
                                return (
                                    <div
                                        key={item.id}
                                        className="group/parent relative flex items-center gap-2"
                                    >
                                        <div className="invisible flex flex-col gap-0.5">
                                            <Button
                                                variant="ghost"
                                                className="aspect-square h-6 w-6 p-1"
                                                // onClick={() =>
                                                //     moveItem(index, "up")
                                                // }
                                                // disabled={index === 0}
                                            >
                                                <IconArrowUp size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                className="aspect-square h-6 w-6 p-1"
                                                // onClick={() =>
                                                //     moveItem(index, "down")
                                                // }
                                                // disabled={
                                                //     index ===
                                                //     selectedItems.length - 1
                                                // }
                                            >
                                                <IconArrowDown size={16} />
                                            </Button>
                                        </div>
                                        <label
                                            className={cn(
                                                "flex w-full select-none items-center gap-3 p-2",
                                                selectedItems.length === 10
                                                    ? "cursor-not-allowed opacity-50"
                                                    : "cursor-pointer hover:bg-accent"
                                            )}
                                        >
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="h-12 w-12 rounded-md object-cover"
                                            />
                                            <div className="flex flex-col">
                                                <span className="font-medium">
                                                    {item.title}
                                                </span>
                                            </div>
                                            <Checkbox
                                                className="ml-auto"
                                                checked={false}
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        addItem(item);
                                                    }
                                                }}
                                                disabled={
                                                    selectedItems.length >= 10
                                                }
                                            />
                                        </label>
                                    </div>
                                );
                            })}
                    </ScrollArea>
                </div>
            </div>
        </CardSetting>
    );
}
